import { useState, useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useApiContext } from "../ApiContext";
import { UserContext } from "./context";

const FORECAST_WARMUP_INTERVAL_MS = 5 * 60 * 1000;

export const UserProvider = ({ children }) => {
  const {
    user: auth0User,
    isAuthenticated,
    isLoading: auth0Loading,
  } = useAuth0();
  const { api } = useApiContext();
  const [backendUser, setBackendUser] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const wasAuthenticatedRef = useRef(false);

  // Load server-side user flags only on login (not on token refresh)
  useEffect(() => {
    if (!isAuthenticated) {
      setBackendUser(null);
      wasAuthenticatedRef.current = false;
      return;
    }

    // Only fetch when transitioning from not-authenticated to authenticated
    if (wasAuthenticatedRef.current || !api) {
      return;
    }

    const loadCurrentUser = async () => {
      setSyncLoading(true);
      try {
        const userData = await api.getCurrentUser();
        setBackendUser(userData);
      } catch (err) {
        console.error("Failed to load user status:", err);
        setBackendUser(null);
      } finally {
        setSyncLoading(false);
        wasAuthenticatedRef.current = true;
      }
    };

    loadCurrentUser();
  }, [isAuthenticated, api]);

  // Keep the forecast Cloud Run container warm only while an entitled user is
  // actively viewing the app. The relay is intentionally best-effort: it must
  // never affect login, navigation, or the rest of the dashboard.
  useEffect(() => {
    if (!api || !isAuthenticated || !backendUser?.forecastDashboardEnabled) {
      return undefined;
    }

    const warmForecast = () => {
      if (document.visibilityState !== "visible") return;
      api.warmForecast().catch(() => {});
    };

    warmForecast();
    const interval = window.setInterval(warmForecast, FORECAST_WARMUP_INTERVAL_MS);
    window.addEventListener("visibilitychange", warmForecast);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("visibilitychange", warmForecast);
    };
  }, [api, backendUser?.forecastDashboardEnabled, isAuthenticated]);

  // Combined user object
  const user =
    isAuthenticated && auth0User
      ? {
          // Auth0 data
          ...auth0User,
          // Backend data (overrides if present)
          ...backendUser,
          // Convenience flags
          isAdmin: backendUser?.isAdmin || false,
          forecastDashboardEnabled:
            backendUser?.forecastDashboardEnabled || false,
          forecastAnalysisEnabled:
            backendUser?.forecastAnalysisEnabled || false,
        }
      : null;

  const value = {
    user,
    isAuthenticated,
    isAdmin: user?.isAdmin || false,
    forecastDashboardEnabled: user?.forecastDashboardEnabled || false,
    forecastAnalysisEnabled: user?.forecastAnalysisEnabled || false,
    loading: auth0Loading || syncLoading,
    userSub: user?.sub || null,
    updateLocalProfile: (updates) =>
      setBackendUser((prev) => ({ ...(prev || {}), ...updates })),
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
